/**
 * History — Unified execution log across ALL automation types
 *
 * Features:
 * - Stats bar: total runs, successful, failed, avg duration (30d)
 * - Filters: search, type, status, date range presets
 * - Table with status badge, type badge, name, agent, trigger, started, duration, steps
 * - Expandable rows showing step-by-step execution trace
 * - Export CSV with real file download
 * - Empty state when no executions match filters
 */

import {
  createFileRoute,
  useNavigate,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Search, Download, ChevronDown, ChevronRight, ExternalLink,
  Activity, CheckCircle, XCircle, Timer,
  Wrench, History as HistoryIcon, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExecutionStatusBadge } from '@/components/automate/execution-status-badge';
import { TriggerTypeBadge } from '@/components/automate/trigger-type-badge';
import { AgentIcon } from '@/components/shared/icon-picker';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  type ExecutionStatus,
  type ExecutionType,
  type ExecutionEntry,
} from '@/lib/mock-automate-data';
import { useExecutions } from '@/lib/use-automate';

export const Route = createFileRoute('/automate/history')({
  component: HistoryPage,
});

type DateRange = 'today' | '7d' | '30d' | 'all';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  if (mins < 60) return remainSecs > 0 ? `${mins}m ${remainSecs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

function getDateRangeMs(range: DateRange): number {
  switch (range) {
    case 'today': return 86400000;
    case '7d': return 7 * 86400000;
    case '30d': return 30 * 86400000;
    case 'all': return Infinity;
  }
}

function HistoryPage() {
  // Issue #624 — `/automate/history` is a parent route (TanStack
  // file-route dot-naming makes `history.$executionId.tsx` a child).
  // Without rendering <Outlet/> when the child path is active, the
  // detail page mounted at /automate/history/:id never appeared even
  // though the URL changed. Mirrors the canonical pattern in
  // playbooks.tsx: every hook below MUST run on every render — the
  // route discriminator (`isIndex`) lives AFTER all hooks so the
  // hook order is stable across list <-> detail transitions
  // (avoids React error #300).
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | ExecutionType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ExecutionStatus>('all');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  // Real-data: automation_executions joined with automations.
  //
  // #197 / #205 — the History page is the primary observability
  // surface for a fresh Test Run. `useExecutions` already:
  //   (a) re-fetches on mount with staleTime=0 (so a user who clicked
  //       Test Run on the workflow page and navigated here sees the
  //       new row immediately, not the 5-minute-stale cache);
  //   (b) auto-polls every 2s while any row is in a non-terminal
  //       status, then falls silent on idle tenants.
  // See the hook's docblock for the rationale of moving the poll
  // logic into the hook rather than the page.
  const { data: executions = [] } = useExecutions();

  const filtered = useMemo(() => {
    const rangeMs = getDateRangeMs(dateRange);
    const now = Date.now();
    return executions.filter(ex => {
      if (typeFilter !== 'all' && ex.type !== typeFilter) return false;
      if (statusFilter !== 'all' && ex.status !== statusFilter) return false;
      if (rangeMs !== Infinity && now - new Date(ex.startedAt).getTime() > rangeMs) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        ex.sourceName.toLowerCase().includes(q) ||
        (ex.agentName?.toLowerCase().includes(q) ?? false) ||
        ex.trigger.toLowerCase().includes(q)
      );
    });
  }, [executions, search, typeFilter, statusFilter, dateRange]);

  const { sorted, sortConfig, handleSort } = useTableSort<ExecutionEntry>(filtered, 'startedAt', 'desc');

  // Stats (computed from 30d data regardless of filters)
  const stats = useMemo(() => {
    const thirtyDayMs = 30 * 86400000;
    const now = Date.now();
    const recent = executions.filter(
      ex => now - new Date(ex.startedAt).getTime() <= thirtyDayMs
    );
    const successful = recent.filter(ex => ex.status === 'completed').length;
    const failed = recent.filter(ex => ex.status === 'failed').length;
    const totalDuration = recent.reduce((sum, ex) => sum + ex.durationMs, 0);
    const avgDuration = recent.length > 0 ? Math.round(totalDuration / recent.length) : 0;
    return { total: recent.length, successful, failed, avgDuration };
  }, [executions]);

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExport = () => {
    const header = 'ID,Type,Name,Agent,Status,Trigger,Started At,Completed At,Duration (ms),Steps Completed,Steps Failed,Error';
    const rows = filtered.map(ex => [
      ex.id,
      ex.type,
      ex.sourceName,
      ex.agentName ?? '',
      ex.status,
      ex.trigger,
      ex.startedAt,
      ex.completedAt ?? '',
      ex.durationMs,
      ex.stepsCompleted,
      ex.stepsFailed,
      ex.error ?? '',
    ]);
    const csv = [
      header,
      ...rows.map(r =>
        r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execution-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported execution history as CSV');
  };

  // Issue #624 — render the child detail route (e.g.
  // /automate/history/:executionId) instead of the list when the
  // active path is anything other than the bare list URL. Kept at
  // the END of the function so every hook above runs on every
  // render regardless of which surface is visible.
  const isIndex =
    location.pathname === '/automate/history' ||
    location.pathname === '/automate/history/';
  if (!isIndex) return <Outlet />;

  return (
    <div className="p-6 space-y-6">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-card p-3">
          <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
            <Activity className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Total Runs (30d)</span>
          </div>
          <p className="text-xl font-semibold">{stats.total}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 mb-1">
            <CheckCircle className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Successful</span>
          </div>
          <p className="text-xl font-semibold text-emerald-700 dark:text-emerald-300">{stats.successful}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <div className="flex items-center gap-1.5 text-red-700 dark:text-red-300 mb-1">
            <XCircle className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Failed</span>
          </div>
          <p className="text-xl font-semibold text-red-700 dark:text-red-300">{stats.failed}</p>
        </div>
        <div className="rounded-lg border bg-card p-3">
          <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
            <Timer className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Avg Duration</span>
          </div>
          <p className="text-xl font-semibold">{formatDuration(stats.avgDuration)}</p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, agent, or trigger..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as 'all' | ExecutionType)}>
          <SelectTrigger aria-label="Filter execution history by type" className="w-[130px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="event">Event</SelectItem>
            <SelectItem value="workflow">Workflow</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="playbook">Playbook</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as 'all' | ExecutionStatus)}>
          <SelectTrigger aria-label="Filter execution history by status" className="w-[130px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dateRange} onValueChange={v => setDateRange(v as DateRange)}>
          <SelectTrigger aria-label="Filter execution history by date range" className="w-[130px]">
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] text-muted-foreground">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 ml-auto"
          title="Export filtered results as CSV"
          onClick={handleExport}
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
      </div>

      {/* Table */}
      {sorted.length > 0 ? (
        <div className="rounded-lg border overflow-x-auto">
          <div className="min-w-[1000px]">
            {/* Header */}
            <div className="grid grid-cols-[28px_80px_80px_minmax(180px,2fr)_minmax(120px,1fr)_minmax(120px,1fr)_80px_70px_70px_70px] gap-3 p-3 bg-muted/50 text-[10px] uppercase tracking-wider">
              <span></span>
              <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Name" sortKey="sourceName" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Agent" sortKey="agentName" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Trigger" sortKey="trigger" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Started" sortKey="startedAt" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Duration" sortKey="durationMs" currentSort={sortConfig} onSort={handleSort} />
              <SortableHeader label="Steps" sortKey="stepsCompleted" currentSort={sortConfig} onSort={handleSort} />
              <span className="text-muted-foreground font-semibold">Quality</span>
            </div>
            {/* Rows */}
            <div className="divide-y">
              {sorted.map(ex => {
                const isExpanded = expandedIds.has(ex.id);
                const hasInlineSteps = Boolean(ex.steps && ex.steps.length > 0);
                return (
                  <div key={ex.id}>
                    {/* Main Row */}
                    <div
                      className={cn(
                        'grid grid-cols-[28px_80px_80px_minmax(180px,2fr)_minmax(120px,1fr)_minmax(120px,1fr)_80px_70px_70px_70px] gap-3 p-3 items-center transition-colors hover:bg-muted/30',
                        isExpanded && 'bg-muted/20',
                      )}
                    >
                      {/* Expand Arrow — toggles inline preview */}
                      <button
                        type="button"
                        onClick={() => {
                          if (hasInlineSteps) {
                            toggleExpanded(ex.id);
                            return;
                          }
                          void navigate({
                            to: '/automate/history/$executionId',
                            params: { executionId: ex.id },
                          } as never);
                        }}
                        className={cn(
                          'flex items-center justify-center rounded',
                          'cursor-pointer hover:bg-muted',
                        )}
                        title={
                          hasInlineSteps
                            ? isExpanded
                              ? 'Hide inline step trace'
                              : 'Show inline step trace'
                            : 'Open full step trace'
                        }
                        aria-label={
                          hasInlineSteps
                            ? isExpanded
                              ? 'Hide inline step trace'
                              : 'Show inline step trace'
                            : 'Open full step trace'
                        }
                      >
                        {hasInlineSteps ? (
                          isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          )
                        ) : (
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </button>
                      {/* Status */}
                      <ExecutionStatusBadge status={ex.status} />
                      {/* Type */}
                      <TriggerTypeBadge type={ex.type} />
                      {/* Name — primary drill-down target.
                          NEW-MAJOR-2 (round 7) — clicking opens the
                          full execution detail at
                          /automate/history/:executionId. */}
                      <button
                        type="button"
                        onClick={() =>
                          navigate({
                            to: '/automate/history/$executionId',
                            params: { executionId: ex.id },
                          } as never)
                        }
                        className="min-w-0 text-left rounded hover:underline focus:outline-none focus:ring-1 focus:ring-ring"
                        title="Open execution detail"
                      >
                        <p className="text-xs font-medium truncate inline-flex items-center gap-1">
                          {ex.sourceName}
                          <ExternalLink
                            className="h-3 w-3 text-muted-foreground"
                            aria-hidden
                          />
                        </p>
                        {ex.error && (
                          <p className="text-[10px] text-red-700 dark:text-red-300 truncate mt-0.5" title={ex.error}>
                            {ex.error}
                          </p>
                        )}
                      </button>
                      {/* Agent */}
                      <div className="flex items-center gap-1.5 min-w-0">
                        {ex.agentIcon && ex.agentIconColor ? (
                          <>
                            <AgentIcon iconName={ex.agentIcon} colorName={ex.agentIconColor} size="sm" />
                            <span className="text-xs text-muted-foreground truncate">{ex.agentName}</span>
                          </>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">--</span>
                        )}
                      </div>
                      {/* Trigger */}
                      <p className="text-[10px] text-muted-foreground truncate" title={ex.trigger}>{ex.trigger}</p>
                      {/* Started */}
                      <span className="text-[10px] text-muted-foreground" title={ex.startedAt ? new Date(ex.startedAt).toLocaleString() : '—'}>
                        {ex.startedAt ? timeAgo(ex.startedAt) : '—'}
                      </span>
                      {/* Duration */}
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {formatDuration(ex.durationMs)}
                      </span>
                      {/* Steps */}
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium">
                          {ex.stepsCompleted}
                        </span>
                        {ex.stepsFailed > 0 && (
                          <>
                            <span className="text-[10px] text-muted-foreground">/</span>
                            <span className="text-[10px] text-red-700 dark:text-red-300 font-medium">
                              {ex.stepsFailed}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Bug #722 — derive from real step counts, not a
                          status-mock. We show "All steps passed" only when
                          every walked step succeeded; "N failed" when at
                          least one step failed; em-dash for non-terminal
                          states. A real supervisor/scorecard verdict is
                          tracked separately at /automate/history/:id and
                          surfaced in the detail drawer. */}
                      <div>
                        {ex.status === 'completed' && ex.stepsFailed === 0 && ex.stepsCompleted > 0 ? (
                          <Badge variant="outline" className="text-[8px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20 gap-0.5" title="All executed steps reported success">
                            <CheckCircle className="h-2.5 w-2.5" /> All steps passed
                          </Badge>
                        ) : ex.stepsFailed > 0 ? (
                          <Badge variant="outline" className="text-[8px] bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30 gap-0.5" title={`${ex.stepsFailed} step(s) failed`}>
                            <XCircle className="h-2.5 w-2.5" /> {ex.stepsFailed} failed
                          </Badge>
                        ) : (
                          <span className="text-[9px] text-muted-foreground" title="No terminal verdict — open detail for full trace">—</span>
                        )}
                      </div>
                    </div>

                    {/* Expanded Step Trace */}
                    {isExpanded && hasInlineSteps && (
                      <div className="bg-muted/10 border-t px-6 py-3">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          Step-by-Step Execution Trace
                        </p>
                        <div className="space-y-1.5">
                          {ex.steps!.map(step => (
                            <div
                              key={step.step}
                              className="flex items-center gap-3 py-1.5 px-3 rounded-md bg-card/50"
                            >
                              {/* Step Number */}
                              <span className="text-[10px] font-mono text-muted-foreground w-5 text-right flex-shrink-0">
                                #{step.step}
                              </span>
                              {/* Step Name */}
                              <span className="text-xs font-medium min-w-[140px] truncate">
                                {step.name}
                              </span>
                              {/* Tool */}
                              {step.toolUsed ? (
                                <Badge
                                  variant="outline"
                                  className="text-[8px] font-mono bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30 flex-shrink-0"
                                >
                                  <Wrench className="h-2 w-2 mr-0.5" />
                                  {step.toolUsed}
                                </Badge>
                              ) : (
                                <span className="text-[9px] text-muted-foreground min-w-[80px]">no tool</span>
                              )}
                              {/* Status */}
                              <ExecutionStatusBadge status={step.status} className="flex-shrink-0" />
                              {/* Duration */}
                              <span className="text-[10px] text-muted-foreground font-mono ml-auto flex-shrink-0">
                                {formatDuration(step.durationMs)}
                              </span>
                            </div>
                          ))}
                          {/* Error message */}
                          {ex.error && (
                            <div className="flex items-start gap-2 py-2 px-3 rounded-md bg-red-500/5 border border-red-500/30 mt-1">
                              <AlertCircle className="h-3.5 w-3.5 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-red-700 dark:text-red-300">{ex.error}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : executions.length === 0 ? (
        <div className="border border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center">
          <HistoryIcon className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No executions yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Workflows run on schedule, event triggers, or manual invocation. Runs will appear here.
          </p>
        </div>
      ) : (
        <div className="border border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center">
          <HistoryIcon className="h-10 w-10 text-muted-foreground/20 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No executions match your filters</p>
          <p className="text-xs text-muted-foreground mt-1">Try adjusting your search, filters, or date range</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 text-xs"
            onClick={() => {
              setSearch('');
              setTypeFilter('all');
              setStatusFilter('all');
              setDateRange('30d');
            }}
          >
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
}
